import { create } from 'zustand';
import {
  Recipe,
  RecipeComponent,
  RecipeStep,
  RecipeCreateRequest,
  RecipeUpdateRequest,
  createRecipe,
  fetchRecipesByRestaurant,
  fetchRecipeById,
  updateRecipe,
  deleteRecipe,
  deleteAllRecipes,
  ComponentImageUpload
} from '../services/recipes/recipesService';
import { RecipeNote, fetchNotesByRecipeId, deleteRecipeNote } from '../services/recipeNotes/recipeNotesService';
import { AllergenDraft } from '../types/RecipeAllergens';
import { saveRecipeAllergens, previewAllergenImport, commitAllergenImport,
  AllergenImportPlan, AllergenImportSelection } from '../services/recipes/recipeAllergensService';

interface RecipeStore {
  // Recipes list state
  recipes: Recipe[];
  isLoadingList: boolean;
  listError: string | null;
  currentRestaurantId: string | null;

  // Recipe detail state
  selectedRecipe: Recipe | null;
  recipeNotes: RecipeNote[];
  isLoadingDetail: boolean;
  detailError: string | null;
  currentRecipeId: string | null;

  // Recipes list actions
  loadRecipes: (restaurantId: string) => Promise<void>;
  reloadRecipes: () => Promise<void>;
  isRecipesLoadedFor: (restaurantId: string) => boolean;
  applyPositions: (updates: Array<{ id: string; position: number }>) => void;

  // Recipe detail actions (always loads recipe + notes together)
  loadRecipeDetail: (recipeId: string) => Promise<void>;
  reloadRecipeDetail: () => Promise<void>;
  isDetailLoadedFor: (recipeId: string) => boolean;
  clearDetail: () => void;

  // Recipe update actions
  updateRecipeData: (
    updates: Partial<Omit<Recipe, 'allergenDetails'>>,
    imageFiles?: File[],
    componentImages?: ComponentImageUpload[]
  ) => Promise<void>;
  deleteRecipeData: () => Promise<void>;
  deleteAllRecipesData: (restaurantId: string) => Promise<number>;

  // Component actions
  reorderComponents: (reorderedComponents: RecipeComponent[]) => Promise<void>;
  reorderSteps: (componentIndex: number, reorderedSteps: RecipeStep[]) => Promise<void>;
  saveComponent: (
    component: RecipeComponent,
    imageFiles: File[],
    mode: 'create' | 'edit',
    editingIndex: number | null
  ) => Promise<void>;
  deleteComponent: (index: number) => Promise<void>;

  // Allergies/Diets actions
  updateAllergies: (allergies: string[]) => Promise<void>;
  updateAllergenDeclaration: (recipeId: string, expectedRevision: string, declaration: AllergenDraft) => Promise<void>;
  createRecipeRecord: (request: RecipeCreateRequest, images?: File[]) => Promise<Recipe>;
  saveRecipeRecord: (request: RecipeUpdateRequest, images?: File[]) => Promise<Recipe>;
  previewAllergenImport: (restaurantId: string, file: File) => Promise<AllergenImportPlan>;
  commitAllergenImport: (restaurantId: string, planId: string, key: string, selections: AllergenImportSelection[]) => Promise<number>;
  acceptServerRecipes: (recipes: Recipe[]) => void;
  updateDiets: (diets: string[]) => Promise<void>;

  // Images actions
  updateImages: (imageFiles: File[]) => Promise<void>;

  // Cost price link actions (null unlinks)
  updateCostPriceLink: (costPriceId: string | null) => Promise<void>;

  // Notes actions
  deleteNote: (noteId: string) => Promise<void>;
  getNotesForComponent: (componentId: string) => RecipeNote[];

  // Clear all
  clear: () => void;
}

// Helper to sort recipes by the manual per-restaurant order. Recipes without a
// position (server not yet returning it) fall to the end, keeping the server's
// updated_at ordering among themselves.
function sortRecipes(recipes: Recipe[]): Recipe[] {
  return [...recipes].sort((a, b) => {
    const posA = a.position !== undefined ? a.position : Infinity;
    const posB = b.position !== undefined ? b.position : Infinity;
    return posA - posB;
  });
}

// Helper to sort components by position
function sortComponents(components: RecipeComponent[]): RecipeComponent[] {
  return [...components].sort((a, b) => {
    const posA = a.position !== undefined ? a.position : Infinity;
    const posB = b.position !== undefined ? b.position : Infinity;
    return posA - posB;
  });
}

// Helper to sort steps within components
function sortStepsInComponents(components: RecipeComponent[]): RecipeComponent[] {
  return components.map(component => ({
    ...component,
    steps: component.steps ? [...component.steps].sort((a, b) => {
      const posA = a.position !== undefined ? a.position : Infinity;
      const posB = b.position !== undefined ? b.position : Infinity;
      return posA - posB;
    }) : []
  }));
}

let scopeGeneration = 0;
let listGeneration = 0;
let detailGeneration = 0;
let listRequest: { restaurantId: string; promise: Promise<void> } | null = null;
let detailRequest: { recipeId: string; promise: Promise<void> } | null = null;

function mergeRecipeRecords(state: RecipeStore, recipes: Recipe[]): Partial<RecipeStore> {
  const byId = new Map(recipes.map(recipe => [recipe.id, recipe]));
  const additions = recipes.filter(recipe => recipe.restaurantId === state.currentRestaurantId &&
    !state.recipes.some(existing => existing.id === recipe.id));
  const selected = state.selectedRecipe && byId.get(state.selectedRecipe.id);
  return {
    recipes: sortRecipes([...state.recipes.map(recipe => byId.get(recipe.id) ?? recipe), ...additions]),
    ...(selected ? { selectedRecipe: { ...selected, components: sortStepsInComponents(sortComponents(selected.components)) } } : {}),
  };
}

export const useRecipeStore = create<RecipeStore>((set, get) => ({
  // Recipes list state
  recipes: [],
  isLoadingList: false,
  listError: null,
  currentRestaurantId: null,

  // Recipe detail state
  selectedRecipe: null,
  recipeNotes: [],
  isLoadingDetail: false,
  detailError: null,
  currentRecipeId: null,

  // Recipes list actions
  loadRecipes: (restaurantId: string) => {
    if (listRequest?.restaurantId === restaurantId) return listRequest.promise;
    const generation = ++listGeneration;
    if (get().currentRestaurantId !== restaurantId) {
      scopeGeneration++;
      get().clearDetail();
      set({ recipes: [], currentRestaurantId: restaurantId });
    }
    set({ isLoadingList: true, listError: null });
    const promise = (async () => { try {
      const fetchedRecipes = await fetchRecipesByRestaurant(restaurantId);
      if (generation !== listGeneration) return;
      set({
        recipes: sortRecipes(fetchedRecipes),
        currentRestaurantId: restaurantId,
        isLoadingList: false,
        listError: null
      });
    } catch (error) {
      if (generation !== listGeneration) return;
      console.error('Failed to load recipes:', error);
      set({
        isLoadingList: false,
        listError: error instanceof Error ? error.message : 'Failed to load recipes'
      });
    } finally {
      if (generation === listGeneration) listRequest = null;
    } })();
    listRequest = { restaurantId, promise };
    return promise;
  },

  reloadRecipes: async () => {
    const state = get();
    if (state.currentRestaurantId) {
      await get().loadRecipes(state.currentRestaurantId);
    }
  },

  isRecipesLoadedFor: (restaurantId: string) => {
    const state = get();
    return state.currentRestaurantId === restaurantId && !state.isLoadingList && !state.listError;
  },

  // Optimistically apply new positions locally so the dropped card stays put
  // (before the network save completes) — avoids the drag-and-drop snap-back.
  applyPositions: (updates: Array<{ id: string; position: number }>) => {
    const posMap = new Map(updates.map(u => [u.id, u.position]));
    set(state => ({
      recipes: sortRecipes(
        state.recipes.map(r =>
          posMap.has(r.id) ? { ...r, position: posMap.get(r.id) as number } : r
        )
      )
    }));
  },

  // Recipe detail actions
  loadRecipeDetail: (recipeId: string) => {
    if (detailRequest?.recipeId === recipeId) return detailRequest.promise;
    const generation = ++detailGeneration;
    set({ isLoadingDetail: true, detailError: null, currentRecipeId: recipeId,
      ...(get().selectedRecipe?.id !== recipeId ? { selectedRecipe: null, recipeNotes: [] } : {}) });
    const promise = (async () => { try {
      // Load recipe and notes together
      const [fetchedRecipe, notes] = await Promise.all([
        fetchRecipeById(recipeId),
        fetchNotesByRecipeId(recipeId)
      ]);
      if (generation !== detailGeneration) return;

      // Sort components and steps
      const sortedComponents = sortStepsInComponents(sortComponents(fetchedRecipe.components || []));

      set({
        selectedRecipe: { ...fetchedRecipe, components: sortedComponents },
        recipeNotes: notes,
        currentRecipeId: recipeId,
        isLoadingDetail: false,
        detailError: null
      });
    } catch (error) {
      if (generation !== detailGeneration) return;
      console.error('Failed to load recipe detail:', error);
      set({
        isLoadingDetail: false,
        detailError: error instanceof Error ? error.message : 'Failed to load recipe'
      });
    } finally {
      if (generation === detailGeneration) detailRequest = null;
    } })();
    detailRequest = { recipeId, promise };
    return promise;
  },

  reloadRecipeDetail: async () => {
    const state = get();
    if (state.currentRecipeId) {
      const recipeId = state.currentRecipeId;
      await get().loadRecipeDetail(recipeId);
    }
  },

  isDetailLoadedFor: (recipeId: string) => {
    const state = get();
    return state.currentRecipeId === recipeId && state.selectedRecipe !== null;
  },

  clearDetail: () => {
    detailGeneration++;
    detailRequest = null;
    set({
      selectedRecipe: null,
      recipeNotes: [],
      currentRecipeId: null,
      isLoadingDetail: false,
      detailError: null
    });
  },

  // Recipe update actions
  updateRecipeData: async (updates, imageFiles, componentImages) => {
    const state = get();
    if (!state.selectedRecipe) return;

    try {
      await updateRecipe(
        { id: state.selectedRecipe.id, ...updates },
        imageFiles,
        componentImages
      );
      await get().reloadRecipeDetail();
      // Also reload list if we have a restaurant loaded
      if (state.currentRestaurantId) {
        await get().reloadRecipes();
      }
    } catch (error) {
      console.error('Failed to update recipe:', error);
      throw error;
    }
  },

  deleteRecipeData: async () => {
    const state = get();
    if (!state.selectedRecipe) return;

    try {
      await deleteRecipe(state.selectedRecipe.id);
      get().clearDetail();
      // Reload list if we have a restaurant loaded
      if (state.currentRestaurantId) {
        await get().reloadRecipes();
      }
    } catch (error) {
      console.error('Failed to delete recipe:', error);
      throw error;
    }
  },

  deleteAllRecipesData: async (restaurantId) => {
    try {
      const deletedCount = await deleteAllRecipes(restaurantId);
      get().clearDetail();
      await get().reloadRecipes();
      return deletedCount;
    } catch (error) {
      console.error('Failed to delete all recipes:', error);
      throw error;
    }
  },

  reorderComponents: async (reorderedComponents) => {
    const state = get();
    if (!state.selectedRecipe) return;

    const updatedComponents = reorderedComponents.map((comp, index) => ({
      ...comp,
      position: index,
    }));

    try {
      await updateRecipe({
        id: state.selectedRecipe.id,
        components: updatedComponents,
      });
      await get().reloadRecipeDetail();
    } catch (error) {
      console.error('Failed to reorder components:', error);
      throw error;
    }
  },

  reorderSteps: async (componentIndex, reorderedSteps) => {
    const state = get();
    if (!state.selectedRecipe) return;

    const updatedComponents = [...state.selectedRecipe.components];
    updatedComponents[componentIndex] = {
      ...updatedComponents[componentIndex],
      steps: reorderedSteps,
    };

    try {
      await updateRecipe({
        id: state.selectedRecipe.id,
        components: updatedComponents,
      });
      await get().reloadRecipeDetail();
    } catch (error) {
      console.error('Failed to reorder steps:', error);
      throw error;
    }
  },

  saveComponent: async (component, imageFiles, mode, editingIndex) => {
    const state = get();
    if (!state.selectedRecipe) return;

    const updatedComponent: RecipeComponent = {
      ...component,
      images: component.images || [],
    };

    let updatedComponents: RecipeComponent[];
    let targetComponentIndex: number;

    if (mode === 'create') {
      updatedComponent.position = state.selectedRecipe.components.length;
      updatedComponents = [...state.selectedRecipe.components, updatedComponent];
      targetComponentIndex = updatedComponents.length - 1;
    } else if (editingIndex !== null) {
      updatedComponent.position = state.selectedRecipe.components[editingIndex].position ?? editingIndex;
      updatedComponents = [...state.selectedRecipe.components];
      updatedComponents[editingIndex] = updatedComponent;
      targetComponentIndex = editingIndex;
    } else {
      return;
    }

    const componentImages: ComponentImageUpload[] = imageFiles.length > 0
      ? [{ componentIndex: targetComponentIndex, imageFiles }]
      : [];

    try {
      await updateRecipe(
        {
          id: state.selectedRecipe.id,
          components: updatedComponents,
        },
        undefined,
        componentImages
      );
      await get().reloadRecipeDetail();
    } catch (error) {
      console.error('Failed to save component:', error);
      throw error;
    }
  },

  deleteComponent: async (index) => {
    const state = get();
    if (!state.selectedRecipe) return;

    const updatedComponents = state.selectedRecipe.components
      .filter((_, i) => i !== index)
      .map((comp, newIndex) => ({
        ...comp,
        position: newIndex,
      }));

    try {
      await updateRecipe({
        id: state.selectedRecipe.id,
        components: updatedComponents,
      });
      await get().reloadRecipeDetail();
    } catch (error) {
      console.error('Failed to delete component:', error);
      throw error;
    }
  },

  updateAllergies: async (allergies) => {
    const state = get();
    if (!state.selectedRecipe) return;

    try {
      await updateRecipe({
        id: state.selectedRecipe.id,
        allergies,
      });
      await get().reloadRecipeDetail();
    } catch (error) {
      console.error('Failed to update allergies:', error);
      throw error;
    }
  },

  updateAllergenDeclaration: async (recipeId, expectedRevision, declaration) => {
    const generation = scopeGeneration;
    const recipe = await saveRecipeAllergens(recipeId, expectedRevision, declaration);
    if (generation === scopeGeneration) get().acceptServerRecipes([recipe]);
  },

  createRecipeRecord: async (request, images) => {
    const generation = scopeGeneration;
    const recipe = await createRecipe(request, images);
    if (generation === scopeGeneration) get().acceptServerRecipes([recipe]);
    return recipe;
  },

  saveRecipeRecord: async (request, images) => {
    const generation = scopeGeneration;
    const recipe = await updateRecipe(request, images);
    if (generation === scopeGeneration) get().acceptServerRecipes([recipe]);
    return recipe;
  },

  previewAllergenImport,
  commitAllergenImport: async (restaurantId, planId, key, selections) => {
    const generation = scopeGeneration;
    const recipes = await commitAllergenImport(restaurantId, planId, key, selections);
    if (generation === scopeGeneration) get().acceptServerRecipes(recipes);
    return recipes.length;
  },

  acceptServerRecipes: (recipes) => {
    // Reads begun before a successful mutation may still contain the old declaration.
    // Invalidate those reads and queue replacements instead of deduplicating onto them.
    const pendingRestaurant = listRequest?.restaurantId;
    const pendingRecipe = detailRequest?.recipeId;
    if (pendingRestaurant) { listGeneration++; listRequest = null; }
    if (pendingRecipe) { detailGeneration++; detailRequest = null; }
    set(state => ({ ...mergeRecipeRecords(state, recipes),
      ...(pendingRestaurant ? { isLoadingList: false } : {}),
      ...(pendingRecipe ? { isLoadingDetail: false } : {}),
    }));
    if (pendingRestaurant && get().currentRestaurantId === pendingRestaurant) void get().loadRecipes(pendingRestaurant);
    if (pendingRecipe && get().currentRecipeId === pendingRecipe) void get().loadRecipeDetail(pendingRecipe);
  },

  updateDiets: async (diets) => {
    const state = get();
    if (!state.selectedRecipe) return;

    try {
      await updateRecipe({
        id: state.selectedRecipe.id,
        diets,
      });
      await get().reloadRecipeDetail();
    } catch (error) {
      console.error('Failed to update diets:', error);
      throw error;
    }
  },

  updateImages: async (imageFiles) => {
    const state = get();
    if (!state.selectedRecipe) return;

    try {
      await updateRecipe(
        { id: state.selectedRecipe.id },
        imageFiles
      );
      await get().reloadRecipeDetail();
    } catch (error) {
      console.error('Failed to update images:', error);
      throw error;
    }
  },

  updateCostPriceLink: async (costPriceId) => {
    const state = get();
    if (!state.selectedRecipe) return;

    try {
      // Send the key explicitly (null = unlink) — the server clears both
      // cost_price_id and the menu_item_name mirror, and mirrors the analysis'
      // revenue item back onto the recipe when linking.
      await updateRecipe({
        id: state.selectedRecipe.id,
        costPriceId,
      });
      await get().reloadRecipeDetail();
      // The recipes list shows the link + its category and is filtered by
      // menuItemName (Active Dishes / menu filter), so the list must reload too.
      if (state.currentRestaurantId) {
        await get().reloadRecipes();
      }
    } catch (error) {
      console.error('Failed to update cost price link:', error);
      throw error;
    }
  },

  deleteNote: async (noteId) => {
    try {
      await deleteRecipeNote(noteId);
      await get().reloadRecipeDetail();
    } catch (error) {
      console.error('Failed to delete note:', error);
      throw error;
    }
  },

  getNotesForComponent: (componentId) => {
    const state = get();
    return state.recipeNotes.filter(n => n.componentId === componentId);
  },

  clear: () => {
    scopeGeneration++;
    listGeneration++;
    detailGeneration++;
    listRequest = null;
    detailRequest = null;
    set({
      recipes: [],
      isLoadingList: false,
      listError: null,
      currentRestaurantId: null,
      selectedRecipe: null,
      recipeNotes: [],
      isLoadingDetail: false,
      detailError: null,
      currentRecipeId: null
    });
  },
}));
